import type { DefaultValues } from "react-hook-form";

import type { AdminStructureDetail } from "../api/structures/structures.types";
import type { PatchAdminStructureGeneralDataRequest } from "../api/structures/structures.types";
import type { AdminStructureGeneralDataForm } from "./structures.schema";
import {
  countFilledPinFields,
  isPinFieldFilled,
} from "./structures-general-data.pin";
import {
  normalizeDecimalStringForApi,
  normalizeDecimalStringForApiOrNull,
} from "@/shared/utils";

const trimToEmpty = (value: string | null | undefined): string =>
  value?.trim() ?? "";

const trimToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

export const mapDetailToGeneralDataForm = (
  detail: AdminStructureDetail,
): DefaultValues<AdminStructureGeneralDataForm> => ({
  type: detail.type ?? undefined,
  responsibleCompany: trimToEmpty(detail.responsibleCompany),
  operationalStatus: detail.operationalStatus ?? "inOperation",
  operationStartDate: trimToEmpty(detail.operationStartDate),
  operationEndDate: detail.operationEndDate?.trim()
    ? detail.operationEndDate.trim()
    : undefined,
  centralPointCoordinateSystem: detail.centralPointCoordinateSystem ?? "sirgas2000",
  centralPointE: trimToEmpty(detail.centralPointE),
  centralPointN: trimToEmpty(detail.centralPointN),
  pinCoordinateSystem: detail.pinCoordinateSystem ?? null,
  pinE: trimToNull(detail.pinE),
  pinN: trimToNull(detail.pinN),
  utmEnabled: detail.utmEnabled,
  utmZone: detail.utmZone ?? undefined,
  utmHemisphere: detail.utmHemisphere ?? undefined,
});

export const mapGeneralDataFormToPatchRequest = (
  data: AdminStructureGeneralDataForm,
): PatchAdminStructureGeneralDataRequest => {
  const pinCoordinateSystem = isPinFieldFilled(data.pinCoordinateSystem)
    ? data.pinCoordinateSystem!
    : null;
  const pinE = normalizeDecimalStringForApiOrNull(data.pinE);
  const pinN = normalizeDecimalStringForApiOrNull(data.pinN);
  const pinFilled = countFilledPinFields({
    pinCoordinateSystem,
    pinE,
    pinN,
  });

  const payload: PatchAdminStructureGeneralDataRequest = {
    type: data.type!,
    responsibleCompany: data.responsibleCompany,
    operationalStatus: data.operationalStatus,
    operationStartDate: data.operationStartDate,
    operationEndDate: data.operationEndDate ?? null,
    centralPointCoordinateSystem: data.centralPointCoordinateSystem,
    centralPointE: normalizeDecimalStringForApi(data.centralPointE),
    centralPointN: normalizeDecimalStringForApi(data.centralPointN),
    utmEnabled: data.utmEnabled,
    utmZone: data.utmEnabled ? (data.utmZone ?? null) : null,
    utmHemisphere: data.utmEnabled ? (data.utmHemisphere ?? null) : null,
  };

  if (pinFilled === 3) {
    payload.pinCoordinateSystem = pinCoordinateSystem;
    payload.pinE = pinE;
    payload.pinN = pinN;
  } else if (pinFilled === 0) {
    payload.pinCoordinateSystem = null;
    payload.pinE = null;
    payload.pinN = null;
  }

  return payload;
};
